// Shared polling hook — consolidates the hand-rolled setTimeout(tick)
// loops used across the app (job pollers, activity feeds). Recursive
// setTimeout (not setInterval) so a slow response never stacks calls;
// pauses while the tab is hidden; interval can adapt to the last result
// (e.g. fast while work is active, slow when idle).

import { useCallback, useEffect, useRef, useState } from 'react';

type IntervalOf<T> = number | ((data: T | null) => number);

export function usePoll<T>(
  fetcher: () => Promise<T>,
  opts: {
    interval: IntervalOf<T>;
    enabled?: boolean;
    pauseWhenHidden?: boolean;
  }
) {
  const { interval, enabled = true, pauseWhenHidden = true } = opts;
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Refs so the loop always sees the latest fetcher/interval without
  // re-arming the effect on every render.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const intervalRef = useRef(interval);
  intervalRef.current = interval;
  const dataRef = useRef<T | null>(null);
  const bumpRef = useRef(0);
  const [bump, setBump] = useState(0);

  const refresh = useCallback(() => {
    bumpRef.current += 1;
    setBump(bumpRef.current);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let inFlight = false;

    const arm = () => {
      // Clear before re-arming: two chains must never both hold a live
      // timeout, or the cadence silently doubles and cleanup can only
      // ever cancel the most recent handle.
      if (timer) clearTimeout(timer);
      const iv = intervalRef.current;
      const ms = typeof iv === 'function' ? iv(dataRef.current) : iv;
      // Interval retries are not forced — they honour pauseWhenHidden.
      timer = setTimeout(() => {
        void tick(false);
      }, ms);
    };

    // force=true: always hit the network. Used for effect-arm (enabled /
    // mount / refresh bump) and visibility wake. force=false: scheduled
    // interval poll — skip while the tab is hidden.
    //
    // WHY force exists (Render Activity board, known-open #13): the old
    // tick always bailed on document.visibilityState === 'hidden' with
    // no fetch, including on the immediate void tick() after a Refresh
    // bump. If visibility is stuck at 'hidden' (CDP/headless sessions,
    // occluded windows, some DevTools setups) — or visibilitychange
    // never fires to wake the loop — the board issued ZERO network
    // requests forever while brandId was truthy and the spinner spun.
    // Live CDP diagnosis: /api/me, /api/brand, /api/catalog all 200;
    // GET /api/ads/render-activity never appeared; Refresh also issued
    // nothing. Pause-when-hidden still applies to background interval
    // polls only.
    const tick = async (force: boolean) => {
      if (cancelled) return;
      // ONE chain, always. `timer` is null for the whole in-flight window
      // (it is only assigned by arm(), which runs after the await), so a
      // visibility wake arriving mid-request would otherwise start a
      // second concurrent chain — and every completion re-arms, so the
      // cadence doubles for the life of the page. The in-flight request
      // re-arms when it settles, so dropping this tick loses nothing.
      if (inFlight) return;
      if (!force && pauseWhenHidden && document.visibilityState === 'hidden') {
        arm();
        return;
      }
      inFlight = true;
      try {
        const next = await fetcherRef.current();
        if (cancelled) return;
        dataRef.current = next;
        setData(next);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError((err as Error).message);
      } finally {
        inFlight = false;
      }
      arm();
    };

    const onVisible = () => {
      // Wake immediately when the tab returns. Safe to call with no timer
      // armed: tick() drops the call if a request is already in flight.
      if (document.visibilityState === 'visible') {
        if (timer) clearTimeout(timer);
        void tick(true);
      }
    };
    if (pauseWhenHidden) document.addEventListener('visibilitychange', onVisible);
    // Immediate arm always forces a fetch (mount / enabled flip / refresh).
    void tick(true);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      if (pauseWhenHidden) document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, pauseWhenHidden, bump]);

  return { data, error, refresh };
}
